// /vote - audience / drop-in voting page. Polls the server for matches
// currently in the vote or reveal phase and lets any visitor score the
// entries. Card-stack UX: one entry at a time, rate 1-5, then next card.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { type VotableMatch, castVotes, fetchVotableMatches } from '~/api/phases';
import { useMatchStore } from '~/game/matchStore';
import { resolveHandle } from '~/lib/handle';
import { buildHead } from '~/seo/site';
import { CountdownTimer } from '~/ui/CountdownTimer';
import { VoteCardStack, type VoteResult } from '~/ui/VoteCardStack';

export const Route = createFileRoute('/vote')({
  head: () =>
    buildHead({
      title: 'Vote on Live Beat Battles',
      description:
        'Drop in and judge live producer battles. Score entries blind, no account needed. Your votes shape the final rankings.',
      path: '/vote',
    }),
  component: VoteFeed,
});

function VoteFeed() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['audience-matches'],
    queryFn: fetchVotableMatches,
    refetchInterval: 5_000,
  });

  return (
    <section className="mx-auto max-w-lg px-4 py-10 space-y-6">
      <header>
        <h2 className="text-3xl font-bold">Vote</h2>
        <p className="text-muted-foreground text-sm">
          Drop in on any ongoing match and score the entries. Your votes push toward the final
          tally.
        </p>
      </header>

      {isLoading && <div className="text-muted-foreground">Loading...</div>}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm">
          Couldn't load matches: {String(error)}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 space-y-4">
          <div>
            <div className="text-lg font-medium">No matches open for voting right now</div>
            <p className="text-sm text-muted-foreground mt-1">
              The vote queue refreshes every few seconds. Two ways to use the moment:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/lobbies"
              className="rounded-md border border-primary/40 bg-primary/5 hover:bg-primary/10 p-4 text-sm space-y-1"
            >
              <div className="font-medium">Watch a live lobby</div>
              <div className="text-xs text-muted-foreground">
                Catch a match before it enters the vote phase.
              </div>
            </Link>
            <Link
              to="/feed"
              className="rounded-md border border-border hover:border-primary/40 p-4 text-sm space-y-1"
            >
              <div className="font-medium">Browse recent submissions</div>
              <div className="text-xs text-muted-foreground">
                Listen to what came out of the last batch of matches.
              </div>
            </Link>
          </div>
        </div>
      )}

      <div className="space-y-10">
        {data?.map((m) => (
          <MatchVoteStack key={m.roomCode} match={m} />
        ))}
      </div>
    </section>
  );
}

function MatchVoteStack({ match }: { match: VotableMatch }) {
  const queryClient = useQueryClient();
  const currentUser = useMatchStore((s) => s.currentUser);
  const [submitted, setSubmitted] = useState(false);

  const mut = useMutation({
    mutationFn: async (votes: VoteResult[]) => {
      const handle = currentUser ?? resolveHandle(null);
      if (votes.length === 0) throw new Error('Pick at least one score before submitting');
      await castVotes({ code: match.roomCode, user: handle, votes });
    },
    onSuccess: () => {
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['audience-matches'] });
    },
  });

  const entries = match.submissions.map((s) => ({
    id: s.submissionId,
    label: s.label,
    audioUrl: s.audioUrl,
    durationSec: s.durationSec,
    isOwn: s.isOwn,
    maxPlaySeconds: match.mode === 'daily' ? 60 : undefined,
  }));

  return (
    <div className="space-y-4">
      {/* Match header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {match.phase} - {match.genre.name}
          </div>
          <div className="font-mono text-primary text-lg tracking-widest">{match.roomCode}</div>
        </div>
        <CountdownTimer deadline={match.transitionsAt} />
      </div>

      {match.mode === 'daily' && (
        <p className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/90">
          Each daily track plays as a 60-second preview. Voting on at least one is enough to keep
          your honor; full-track listening is on the producer's profile.
        </p>
      )}

      {submitted ? (
        <div className="rounded-md border border-green-500/40 bg-green-500/10 p-4 text-sm text-center">
          Thanks, votes submitted.
        </div>
      ) : (
        <VoteCardStack
          entries={entries}
          onSubmit={async (votes) => mut.mutateAsync(votes)}
          disabled={submitted}
        />
      )}
    </div>
  );
}
